import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Shield, Calendar, User, ArrowLeft, Clock, Tag } from 'lucide-react';

interface PostContent {
  id: string;
  title: string;
  content: string;
  excerpt: string;
  author: string;
  lastModified: string;
  status: string;
  tags?: string[];
  featuredImage?: string;
}

const PostDisplay: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [post, setPost] = useState<PostContent | null>(null);
  const [allPosts, setAllPosts] = useState<PostContent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchPost();
  }, [slug]);

  const fetchPost = async () => {
    try {
      setLoading(true);
      
      if (slug) {
        // Try to fetch single post from API
        try {
          const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/cms/public/post/${slug}`);
          if (response.ok) {
            const postData = await response.json();
            setPost(postData);
            return;
          }
        } catch (apiError) {
          console.error('API fetch failed, using fallback data:', apiError);
        }
      } else {
        // Try to fetch all posts from API
        try {
          const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/cms/public/posts`);
          if (response.ok) {
            const postsData = await response.json();
            setAllPosts(postsData);
            return;
          }
        } catch (apiError) {
          console.error('API fetch failed, using fallback data:', apiError);
        }
      }
      
      // Fallback to mock data if API fails
      const mockPosts: PostContent[] = [
        {
          id: '1',
          title: 'Welcome to SkillSeek: Your Gateway to Jamaica\'s Best Talent',
          excerpt: 'Discover how SkillSeek is revolutionizing the way Jamaicans connect with skilled professionals.',
          content: `
            <div class="post-content">
              <p class="lead">We're excited to introduce SkillSeek, Jamaica's premier platform for connecting skilled professionals with clients who need their expertise.</p>
              
              <h3>What Makes SkillSeek Different?</h3>
              <p>Unlike other platforms, SkillSeek is built specifically for the Jamaican market, understanding our unique culture, work ethic, and business environment. We've created a space where local talent can thrive and clients can find exactly what they need.</p>
              
              <h3>For Professionals</h3>
              <ul>
                <li>Create a comprehensive profile showcasing your skills and experience</li>
                <li>Set your own rates and availability</li>
                <li>Build a portfolio that highlights your best work</li>
                <li>Connect directly with potential clients</li>
                <li>Secure payment processing</li>
              </ul>
              
              <h3>For Clients</h3>
              <ul>
                <li>Browse verified professionals across various industries</li>
                <li>Read reviews and ratings from previous clients</li>
                <li>Compare prices and services</li>
                <li>Book services with confidence</li>
                <li>24/7 customer support</li>
              </ul>
              
              <h3>Getting Started</h3>
              <p>Whether you're a skilled professional looking to expand your client base or a client seeking reliable services, getting started on SkillSeek is simple:</p>
              <ol>
                <li>Create your free account</li>
                <li>Complete your profile (professionals) or post your project (clients)</li>
                <li>Start connecting and building relationships</li>
              </ol>
              
              <p>Join the SkillSeek community today and be part of Jamaica's growing digital economy!</p>
            </div>
          `,
          author: 'SkillSeek Team',
          lastModified: '2024-01-16T09:15:00Z',
          status: 'published',
          tags: ['announcement', 'getting-started', 'platform'],
          featuredImage: '/images/skillseek-welcome.jpg'
        },
        {
          id: '2',
          title: 'Top 10 Most In-Demand Skills in Jamaica 2024',
          excerpt: 'Explore the skills that are driving Jamaica\'s economy forward and discover opportunities in high-growth sectors.',
          content: `
            <div class="post-content">
              <p class="lead">As Jamaica's economy continues to evolve, certain skills are becoming increasingly valuable. Here are the top 10 most in-demand skills for 2024.</p>
              
              <h3>1. Digital Marketing</h3>
              <p>With businesses moving online, digital marketing expertise is crucial. This includes social media management, SEO, content creation, and online advertising.</p>
              
              <h3>2. Web Development</h3>
              <p>From e-commerce sites to business applications, web developers are in high demand across all industries.</p>
              
              <h3>3. Graphic Design</h3>
              <p>Visual communication is more important than ever. Skilled graphic designers are needed for branding, marketing materials, and digital content.</p>
              
              <h3>4. Content Writing</h3>
              <p>Quality content drives online success. Content writers who can create engaging, SEO-friendly material are highly sought after.</p>
              
              <h3>5. Photography and Videography</h3>
              <p>Visual content creation for social media, marketing, and events continues to grow in demand.</p>
              
              <h3>6. Accounting and Bookkeeping</h3>
              <p>Small businesses need financial management support, making accounting skills always valuable.</p>
              
              <h3>7. Customer Service</h3>
              <p>Excellent customer service can make or break a business, especially in Jamaica's service-oriented economy.</p>
              
              <h3>8. Project Management</h3>
              <p>As businesses take on more complex projects, skilled project managers are essential for success.</p>
              
              <h3>9. Translation Services</h3>
              <p>With Jamaica's tourism industry and international business connections, translation skills are increasingly valuable.</p>
              
              <h3>10. Tutoring and Training</h3>
              <p>Education and skill development never go out of style, making tutoring and training services consistently in demand.</p>
              
              <h3>Developing Your Skills</h3>
              <p>If you have expertise in any of these areas, SkillSeek is the perfect platform to showcase your skills and connect with clients. If you're looking to develop new skills, consider investing in these high-demand areas.</p>
            </div>
          `,
          author: 'Career Expert',
          lastModified: '2024-01-14T14:30:00Z',
          status: 'published',
          tags: ['skills', 'careers', 'trends', 'jamaica'],
          featuredImage: '/images/top-skills-2024.jpg'
        }
      ];

      setAllPosts(mockPosts);

      if (slug) {
        const foundPost = mockPosts.find(p => 
          p.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') === slug
        );
        
        if (foundPost && foundPost.status === 'published') {
          setPost(foundPost);
        } else {
          setError('Post not found');
        }
      }
    } catch (err) {
      setError('Failed to load post');
      console.error('Error fetching post:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (error || (!post && slug)) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center">
        <div className="text-center">
          <h1 className="display-1 fw-bold text-muted">404</h1>
          <p className="fs-3 text-muted">Post not found</p>
          <a href="/posts" className="btn btn-primary">
            <ArrowLeft size={16} className="me-2" />
            Back to Blog
          </a>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Navigation Header */}
      <nav className="navbar navbar-expand-lg navbar-light bg-white shadow-sm sticky-top">
        <div className="container">
          <a className="navbar-brand d-flex align-items-center" href="/">
            <Shield className="text-primary me-2" size={28} />
            <span className="fw-bold text-dark">SkillSeek</span>
          </a>
          
          <div className="collapse navbar-collapse">
            <ul className="navbar-nav me-auto">
              <li className="nav-item">
                <a className="nav-link" href="/">Home</a>
              </li>
              <li className="nav-item">
                <a className="nav-link" href="/about">About</a>
              </li>
              <li className="nav-item">
                <a className="nav-link active" href="/posts">Blog</a>
              </li>
            </ul>
            <div className="d-flex gap-2">
              <a href="/login" className="btn btn-outline-primary">Login</a>
              <a href="/register" className="btn btn-primary">Sign Up</a>
            </div>
          </div>
        </div>
      </nav>

      {/* Post Content */}
      {post ? (
        <article className="container py-5">
          <div className="row justify-content-center">
            <div className="col-lg-8">
              <div className="mb-4">
                <a href="/posts" className="btn btn-link p-0 text-decoration-none">
                  <ArrowLeft size={16} className="me-2" />
                  Back to Blog
                </a>
              </div>

              <header className="mb-5">
                <h1 className="display-5 fw-bold text-dark mb-3">{post.title}</h1>
                
                <div className="d-flex align-items-center text-muted mb-3">
                  <User size={16} className="me-2" />
                  <span className="me-4">{post.author}</span>
                  <Calendar size={16} className="me-2" />
                  <span>{new Date(post.lastModified).toLocaleDateString()}</span>
                </div>
                
                {post.tags && (
                  <div className="d-flex gap-2 mb-4">
                    {post.tags.map(tag => (
                      <span key={tag} className="badge bg-primary bg-opacity-10 text-primary border">
                        <Tag size={12} className="me-1" />
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
              </header>

              <div className="post-content" dangerouslySetInnerHTML={{ __html: post.content }} />
            </div>
          </div>
        </article>
      ) : (
        <div className="container py-5">
          <div className="row justify-content-center">
            <div className="col-lg-8">
              <div className="text-center mb-5">
                <h1 className="display-4 fw-bold text-dark">SkillSeek Blog</h1>
                <p className="lead text-muted">Insights, tips, and updates from the SkillSeek community</p>
              </div>

              {allPosts.filter(p => p.status === 'published').map((post) => (
                <article key={post.id} className="card border-0 shadow-sm mb-4">
                  <div className="card-body p-4">
                    <div className="d-flex align-items-center text-muted small mb-2">
                      <User size={14} className="me-1" />
                      <span className="me-3">{post.author}</span>
                      <Calendar size={14} className="me-1" />
                      <span>{new Date(post.lastModified).toLocaleDateString()}</span>
                    </div>
                    
                    <h2 className="h4 fw-bold mb-3">
                      <a 
                        href={`/posts/${post.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')}`}
                        className="text-decoration-none text-dark"
                      >
                        {post.title}
                      </a>
                    </h2>
                    
                    <p className="text-muted mb-3">{post.excerpt}</p>
                    
                    <div className="d-flex align-items-center justify-content-between">
                      <div className="d-flex gap-2">
                        {post.tags?.map(tag => (
                          <span key={tag} className="badge bg-light text-dark border">
                            <Tag size={10} className="me-1" />
                            {tag}
                          </span>
                        ))}
                      </div>
                      
                      <a 
                        href={`/posts/${post.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')}`}
                        className="btn btn-outline-primary btn-sm"
                      >
                        Read More
                      </a>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="bg-dark text-light py-5 mt-5">
        <div className="container">
          <div className="row">
            <div className="col-md-6">
              <div className="d-flex align-items-center mb-3">
                <Shield className="text-primary me-2" size={24} />
                <span className="fw-bold">SkillSeek</span>
              </div>
              <p className="text-muted">Connecting skilled professionals with clients across Jamaica.</p>
            </div>
            <div className="col-md-6 text-md-end">
              <small className="text-muted">© 2024 SkillSeek. All rights reserved.</small>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PostDisplay; 